import { Outlet, NavLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, Home, Settings, FileDown, ClipboardList, AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { apiGet, apiPatch } from '../shared/apiClient';
import type { Squadra } from '../shared/types';
import { MustChangePasswordModal } from './MustChangePasswordModal';
import { AvvisiTicker } from './AvvisiTicker';
import { AvvisiModalSquadra } from './AvvisiModalSquadra';
import { APP_VERSION } from '../shared/constants';

export function Layout() {
  const { user, logout, refreshUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const squadraQuery = useQuery({
    queryKey: ['squadra-me'],
    queryFn: async () => {
      if (!user?.squadraId) return null;
      const { data } = await apiGet<Squadra>('/squadre/me');
      return data;
    },
    enabled: !!user?.squadraId,
  });

  const isDisponibile = squadraQuery.data?.disponibile ?? true;

  const toggleMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const { data } = await apiPatch<Squadra>('/squadre/me/disponibile', { disponibile: next });
      return data;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: ['squadra-me'] });
      const prev = queryClient.getQueryData<Squadra>(['squadra-me']);
      if (prev) queryClient.setQueryData<Squadra>(['squadra-me'], { ...prev, disponibile: next });
      return { prev };
    },
    onSuccess: (updated) => {
      toast({
        type: updated.disponibile ? 'success' : 'warning',
        message: updated.disponibile ? 'Stato: IN SERVIZIO (Verde)' : 'Stato: IN PAUSA (Giallo)',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['squadra-me'] });
    },
  });

  const onLogout = async () => {
    await logout();
    toast({ type: 'info', message: 'Sessione chiusa' });
  };

  return (
    <div className="min-h-screen bg-slate-50/70 text-slate-900 flex flex-col items-center linear-squadra-root">
      {/* Header fisso con effetto blur stile Linear */}
      <header className="sticky top-0 z-40 w-full ln-header-squadra safe-top shadow-xs">
        <div className="max-w-4xl mx-auto px-3.5 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold shadow-xs shrink-0">
              <span className="text-xs font-black">R</span>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider">Road Hub · Squadra</span>
                <span className="text-[10px] font-extrabold text-slate-600 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200">{APP_VERSION}</span>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                    isDisponibile
                      ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                      : 'text-amber-800 bg-amber-50 border-amber-200'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${isDisponibile ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                  <span>{isDisponibile ? 'In servizio' : 'In pausa'}</span>
                </span>
              </div>
              <h1 className="text-sm sm:text-base font-extrabold text-slate-900 leading-tight truncate">
                {user?.nome || 'Squadra'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Toggle rapido disponibilità in alto affianco al pulsante esci */}
            <button
              type="button"
              onClick={() => toggleMutation.mutate(!isDisponibile)}
              disabled={toggleMutation.isPending}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 border active:scale-95 ${
                isDisponibile
                  ? 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100'
                  : 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:bg-emerald-100'
              }`}
              title={isDisponibile ? 'Clicca per mettere la squadra in pausa (Giallo)' : 'Clicca per tornare in servizio (Verde)'}
            >
              <span className={`w-2 h-2 rounded-full ${isDisponibile ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              <span className="hidden xs:inline">{isDisponibile ? 'In servizio' : 'In pausa'}</span>
            </button>

            <button
              type="button"
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition border border-transparent hover:border-rose-100"
              onClick={onLogout}
              title="Disconnetti"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline text-xs font-semibold">Esci</span>
            </button>
          </div>
        </div>
      </header>

      {/* Area contenuto centrale max-w-4xl per ottima resa su tablet e cellulare */}
      <main className="w-full max-w-4xl px-3.5 sm:px-4 py-3 sm:py-4 pb-44 sm:pb-48 flex-1 animate-fade-in">
        <Outlet />
      </main>

      {/* Marquee Ticker avvisi di centrale (immediatamente sopra la bottom bar) */}
      <AvvisiTicker />

      {/* Modal di notifica e conferma lettura avvisi di centrale */}
      <AvvisiModalSquadra />

      {/* Bottom Bar fissa touch-friendly: 4 colonne standard, 5 colonne se abilitata a segnalatore */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 ln-bottom-nav safe-bottom">
        <div className={`max-w-4xl mx-auto grid ${squadraQuery.data?.abilitaSegnalazioni ? 'grid-cols-5' : 'grid-cols-4'} gap-1 px-2 py-1`}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `ln-bottom-nav-item flex flex-col items-center justify-center gap-1 py-1.5 px-1 text-[11px] font-semibold transition ${
                isActive ? 'active' : ''
              }`
            }
          >
            <Home className="w-5 h-5" />
            <span>Dashboard</span>
          </NavLink>

          <NavLink
            to="/odl"
            className={({ isActive }) =>
              `ln-bottom-nav-item flex flex-col items-center justify-center gap-1 py-1.5 px-1 text-[11px] font-semibold transition ${
                isActive ? 'active' : ''
              }`
            }
          >
            <ClipboardList className="w-5 h-5" />
            <span>ODL</span>
          </NavLink>

          {squadraQuery.data?.abilitaSegnalazioni && (
            <NavLink
              to="/segnalazioni"
              className={({ isActive }) =>
                `ln-bottom-nav-item flex flex-col items-center justify-center gap-1 py-1.5 px-1 text-[11px] font-semibold transition ${
                  isActive ? 'active' : ''
                }`
              }
            >
              <AlertTriangle className="w-5 h-5" />
              <span>Segnalazioni</span>
            </NavLink>
          )}

          <NavLink
            to="/utilita"
            className={({ isActive }) =>
              `ln-bottom-nav-item flex flex-col items-center justify-center gap-1 py-1.5 px-1 text-[11px] font-semibold transition ${
                isActive ? 'active' : ''
              }`
            }
          >
            <FileDown className="w-5 h-5" />
            <span>Utilità</span>
          </NavLink>

          <NavLink
            to="/impostazioni"
            className={({ isActive }) =>
              `ln-bottom-nav-item flex flex-col items-center justify-center gap-1 py-1.5 px-1 text-[11px] font-semibold transition ${
                isActive ? 'active' : ''
              }`
            }
          >
            <Settings className="w-5 h-5" />
            <span>Impostazioni</span>
          </NavLink>
        </div>
      </nav>

      <MustChangePasswordModal
        isOpen={!!user?.mustChangePassword}
        onSuccess={() => refreshUser()}
      />
    </div>
  );
}